/**
 * SA Section Manager
 * 主入口：管理和注册所有 SA Section Web Components
 * 
 * 架构说明：
 * - 每个 section 是一个独立的 Web Component
 * - 使用 ES modules (type=module) 动态加载
 * - 支持降级方案：如果不支持动态 import，使用 script 标签加载
 * - 统一的组件注册和管理机制
 */

// 已知的所有组件名称列表（与 components/index.js 保持同步）
const knownComponents = [
  'sa-blog-article',
  'sa-blog-reading-progress',
  'sa-blog-table-of-contents',
  'sa-blog-ai-summary',
  'sa-blog-article-feedback',
  'sa-blog-newsletter',
  'sa-blog-write-for-us',
  'sa-blog-article-editor'
];

class SASectionManager {
  constructor() {
    this.components = new Map();
    this.loadedComponents = new Set();
    this.loadingComponents = new Set();
    this.observer = null;
    this.basePath = this.getBasePath();
    this.supportsDynamicImport = this.checkDynamicImportSupport();
  }

  /**
   * 获取组件的基础路径
   */
  getBasePath() {
    if (typeof window !== 'undefined' && window.SA_COMPONENTS_BASE_PATH) {
      return window.SA_COMPONENTS_BASE_PATH;
    }
    
    const script = document.querySelector('script[data-comp="sa-section-manager"]');
    if (script && script.src) {
      const scriptPath = script.src;
      const baseUrl = scriptPath.substring(0, scriptPath.lastIndexOf('/') + 1);
      return baseUrl;
    }
    
    return '/assets/';
  }

  /**
   * 检测浏览器是否支持动态 import
   */
  checkDynamicImportSupport() {
    try {
      if (typeof window !== 'undefined') {
        const script = document.currentScript;
        if (script && script.type === 'module') {
          return true;
        }
        return 'noModule' in HTMLScriptElement.prototype;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * 使用动态 import 加载组件
   */
  async loadComponentWithImport(componentName) {
    try {
      const module = await import(`${this.basePath}${componentName}.js`);
      return module.default || module[this.toPascalCase(componentName)];
    } catch (error) {
      console.error(`Failed to load ${componentName} with dynamic import:`, error);
      throw error;
    }
  }

  /**
   * 使用 script 标签降级加载组件
   */
  loadComponentWithScript(componentName) {
    return new Promise((resolve, reject) => {
      if (customElements.get(componentName)) {
        const existingComponent = this.components.get(componentName);
        if (existingComponent) {
          resolve(existingComponent);
          return;
        }
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.src = `${this.basePath}${componentName}.js`;
      
      let resolved = false;
      
      const loadHandler = (event) => {
        if (event.detail && event.detail.componentName === componentName) {
          resolved = true;
          clearInterval(checkInterval);
          window.removeEventListener('component-loaded', loadHandler);
          const ComponentClass = customElements.get(componentName);
          if (ComponentClass) {
            resolve(ComponentClass);
          } else {
            reject(new Error(`Component ${componentName} loaded but not registered`));
          }
        }
      };
      
      window.addEventListener('component-loaded', loadHandler);
      
      const checkInterval = setInterval(() => {
        if (customElements.get(componentName)) {
          if (!resolved) {
            resolved = true;
            clearInterval(checkInterval);
            window.removeEventListener('component-loaded', loadHandler);
            resolve(customElements.get(componentName));
          }
        }
      }, 50);

      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          clearInterval(checkInterval);
          window.removeEventListener('component-loaded', loadHandler);
          if (customElements.get(componentName)) {
            resolve(customElements.get(componentName));
          } else {
            reject(new Error(`Timeout loading ${componentName}`));
          }
        }
      }, 5000);

      script.onerror = () => {
        if (!resolved) {
          resolved = true;
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          window.removeEventListener('component-loaded', loadHandler);
          reject(new Error(`Failed to load script for ${componentName}`));
        }
      };

      script.onload = () => {
        setTimeout(() => {
          if (!resolved && !customElements.get(componentName)) {
            console.warn(`Script loaded for ${componentName} but component not registered yet`);
          }
        }, 100);
      };

      document.head.appendChild(script);
    });
  }

  /**
   * 动态加载组件模块
   */
  async loadComponent(componentName) {
    if (this.loadedComponents.has(componentName)) {
      return this.components.get(componentName);
    }

    if (this.loadingComponents.has(componentName)) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.loadedComponents.has(componentName)) {
            clearInterval(checkInterval);
            resolve(this.components.get(componentName));
          }
        }, 50);
      });
    }

    this.loadingComponents.add(componentName);

    try {
      let ComponentClass;

      if (this.supportsDynamicImport) {
        try {
          ComponentClass = await this.loadComponentWithImport(componentName);
        } catch (importError) {
          console.warn(`Dynamic import failed for ${componentName}, falling back to script tag:`, importError);
          ComponentClass = await this.loadComponentWithScript(componentName);
        }
      } else {
        ComponentClass = await this.loadComponentWithScript(componentName);
      }

      this.register(componentName, ComponentClass);
      return ComponentClass;
    } catch (error) {
      console.error(`Failed to load component ${componentName}:`, error);
      this.loadingComponents.delete(componentName);
      throw error;
    }
  }

  /**
   * 注册一个组件
   */
  register(name, componentClass) {
    if (this.components.has(name)) {
      console.warn(`Component ${name} is already registered`);
      return;
    }

    if (customElements.get(name)) {
      console.warn(`Custom element ${name} is already defined`);
      return;
    }

    try {
      customElements.define(name, componentClass);
      this.components.set(name, componentClass);
      this.loadedComponents.add(name);
      this.loadingComponents.delete(name);
      
      // Dispatch event for script tag fallback
      window.dispatchEvent(new CustomEvent('component-loaded', {
        detail: { componentName: name }
      }));
      
      console.log(`✓ Component ${name} registered`);
    } catch (error) {
      console.error(`Failed to register component ${name}:`, error);
      this.loadingComponents.delete(name);
    }
  }

  /**
   * 发现页面中使用的组件标签
   */
  discoverComponents() {
    const componentNames = new Set();
    
    knownComponents.forEach(componentName => {
      if (document.querySelector(componentName)) {
        componentNames.add(componentName);
      }
    });
    
    const elementsWithData = document.querySelectorAll('[data-component^="sa-"]');
    elementsWithData.forEach(el => {
      if (el.dataset?.component) {
        componentNames.add(el.dataset.component);
      }
    });

    return Array.from(componentNames);
  }

  /**
   * 自动发现并加载页面中的组件
   */
  async autoLoad() {
    const componentNames = this.discoverComponents();
    
    const componentsToLoad = componentNames.filter(name => {
      if (customElements.get(name)) {
        console.log(`Component ${name} is already registered, skipping`);
        return false;
      }
      return true;
    });
    
    if (componentsToLoad.length === 0) {
      console.log('No new components to load');
      return;
    }

    console.log(`Discovering components: ${componentsToLoad.join(', ')}`);
    
    const loadPromises = componentNames.map(name => 
      this.loadComponent(name).catch(err => {
        console.warn(`Failed to auto-load ${name}:`, err);
      })
    );

    await Promise.all(loadPromises);
    console.log('All components loaded');
  }

  /**
   * 使用 MutationObserver 监听 DOM 变化，自动加载新组件
   */
  observe() {
    if (this.observer) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      const newComponents = new Set();
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName && node.tagName.toLowerCase().startsWith('sa-')) {
              const componentName = node.tagName.toLowerCase();
              if (!this.loadedComponents.has(componentName) && 
                  !this.loadingComponents.has(componentName)) {
                newComponents.add(componentName);
              }
            }

            if (node.dataset?.component && node.dataset.component.startsWith('sa-')) {
              const componentName = node.dataset.component;
              if (!this.loadedComponents.has(componentName) && 
                  !this.loadingComponents.has(componentName)) {
                newComponents.add(componentName);
              }
            }

            if (node.querySelectorAll) {
              const children = node.querySelectorAll('*');
              children.forEach(child => {
                const tagName = child.tagName?.toLowerCase();
                const componentName = child.dataset?.component || tagName;
                if (componentName && componentName.startsWith('sa-') &&
                    !this.loadedComponents.has(componentName) && 
                    !this.loadingComponents.has(componentName)) {
                  newComponents.add(componentName);
                }
              });
            }
          }
        });
      });

      newComponents.forEach(name => {
        this.loadComponent(name).catch(console.warn);
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * 工具方法：转换为 PascalCase
   */
  toPascalCase(str) {
    return str
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  /**
   * 初始化管理器
   */
  async init() {
    console.log(`Using ${this.supportsDynamicImport ? 'dynamic import' : 'script tag fallback'} for component loading`);
    
    await this.autoLoad();
    this.observe();
    
    console.log('SA Section Manager initialized');
  }
}

// 创建全局管理器实例
const saSectionManager = new SASectionManager();

// DOM 加载完成后自动初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => saSectionManager.init());
} else {
  saSectionManager.init();
}

// 导出管理器（用于手动控制）
export default saSectionManager;

